'use client';

import { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';

import { Button } from '@northladder/design-system';
import { Text } from '@northladder/i18n';
import { TTextLocalized } from '@northladder/utilities';

import { PlayPauseSVGIcon } from '~/icons';

interface INoiseDetectionModalProps {
  isOpen: boolean;
  message: TTextLocalized;
  onClose: () => void;
  onRetry: () => void;
  onWorking: () => void;
  playAudioLabel: TTextLocalized;
  referenceAudioPath: string;
  retryLabel: TTextLocalized;
  title: TTextLocalized;
  workingLabel: TTextLocalized;
}

/**
 * -----------------------------------------------------------------------------
 * Modal component that displays when noise is detected during audio testing.
 * Allows users to play the recorded audio, retry the test, or mark as working.
 */
export default function NoiseDetectionModal({
  isOpen,
  message,
  onClose,
  onRetry,
  onWorking,
  playAudioLabel,
  referenceAudioPath,
  retryLabel,
  title,
  workingLabel,
}: INoiseDetectionModalProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    if (isOpen && referenceAudioPath) {
      if (audioRef.current) {
        audioRef.current.setAttribute('playsinline', 'true');
        audioRef.current.load();
      }
    }
  }, [isOpen, referenceAudioPath]);

  useEffect(() => {
    if (!isOpen) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }

      setIsPlaying(false);
    }

    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
    };
  }, [isOpen]);

  const resumeAudioContextIfNeeded = async (): Promise<void> => {
    if (!audioContextRef.current) {
      const AudioContextClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;

      if (AudioContextClass) {
        audioContextRef.current = new AudioContextClass();

        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume().catch(() => {});
        }
      }
    } else if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume().catch(() => {});
    }
  };

  const handlePlayAudio = async () => {
    if (!audioRef.current) {
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    try {
      await resumeAudioContextIfNeeded();

      await audioRef.current.play();
      setIsPlaying(true);
    } catch (error) {
      if (audioRef.current) {
        audioRef.current.load();

        try {
          await resumeAudioContextIfNeeded();
          await audioRef.current.play();
          setIsPlaying(true);
        } catch (retryError) {
          setIsPlaying(false);
        }
      }
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const handleAudioError = () => {
    setIsPlaying(false);
  };

  const handleOnRetry = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    setIsPlaying(false);
    onRetry();
  };

  const handleOnWorking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    setIsPlaying(false);
    onWorking();
  };

  return (
    <Transition appear as={Fragment} show={isOpen}>
      <Dialog as="div" className="relative z-[100]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/75" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div
            className="
              flex min-h-full items-center justify-center p-4 text-center
            "
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="
                  w-full max-w-sm transform overflow-hidden rounded-2xl
                  bg-white p-6 text-start align-middle shadow-xl transition-all
                  dark:bg-gray-600
                "
              >
                <div className="bottom-5 h-auto w-full border-teal-600">
                  <Dialog.Title className="text-3xl font-semibold">
                    <Text text={title} />
                  </Dialog.Title>

                  <Dialog.Description
                    className="
                      mt-2 text-sm leading-4 text-zinc-600 dark:text-zinc-300
                    "
                  >
                    <Text text={message} />
                  </Dialog.Description>

                  {referenceAudioPath ? (
                    <div className="mt-4 flex items-center justify-center">
                      <Button
                        onClick={handlePlayAudio}
                        type="button"
                        variant="outline"
                      >
                        <PlayPauseSVGIcon
                          className="size-5"
                          isPlaying={isPlaying}
                        />
                        <Text text={playAudioLabel} />
                      </Button>
                    </div>
                  ) : null}

                  {referenceAudioPath ? (
                    <audio
                      ref={audioRef}
                      crossOrigin="anonymous"
                      onEnded={handleAudioEnded}
                      onError={handleAudioError}
                      preload="auto"
                      src={referenceAudioPath}
                    >
                      <track kind="captions" />
                    </audio>
                  ) : null}

                  <div className="mt-4 flex w-full justify-between gap-4">
                    <Button
                      onClick={handleOnRetry}
                      type="button"
                      variant="outline"
                    >
                      <Text text={retryLabel} />
                    </Button>

                    <Button
                      className="z-50 w-auto"
                      onClick={handleOnWorking}
                      type="button"
                      variant="default"
                    >
                      <Text text={workingLabel} />
                    </Button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
